import React from 'react';
import { Navigate } from 'react-router-dom';
import { useEntrepreneurAuth } from '@/hooks/useAuthHooks';
import { EntrepreneurState } from '@/types/auth';
import { ePath } from '@/utils/paths';
import { carrying } from '@/lib/accountActivation';

interface StateGuardProps {
  allowedStates: EntrepreneurState[];
  redirectPath: string;
  children: React.ReactNode;
}

export const StateGuard: React.FC<StateGuardProps> = ({ 
  allowedStates, 
  redirectPath, 
  children 
}) => {
  const { entrepreneurUser } = useEntrepreneurAuth();

  if (!entrepreneurUser) {
    return <Navigate to={ePath('/login')} replace />;
  }

  if (!allowedStates.includes(entrepreneurUser.entrepreneur_state)) {
    return <Navigate to={redirectPath} replace />;
  }

  return <>{children}</>;
};

// Pre-configured guards for common scenarios
export const ApprovedOnlyGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { entrepreneurUser } = useEntrepreneurAuth();

  if (!entrepreneurUser) {
    return <Navigate to={carrying(ePath('/login'))} replace />;
  }

  // Redirect based on current EntrepreneurState
  if ([
    EntrepreneurState.PENDING_LINKEDIN_PARSE,
    EntrepreneurState.PENDING_CV_UPLOAD,
    EntrepreneurState.PENDING_ONBOARDING
  ].includes(entrepreneurUser.entrepreneur_state)) {
    return <Navigate to={carrying(ePath('/onboarding'))} replace />;
  }

  // PENDING_FAILED_PRESCORE: Check attempts to determine page
  if (entrepreneurUser.entrepreneur_state === EntrepreneurState.PENDING_FAILED_PRESCORE) {
    const attempts = entrepreneurUser.prescore_attempts ?? 0;
    if (attempts < 2) {
      // Can retry - redirect to onboarding
      return <Navigate to={carrying(ePath('/onboarding'))} replace />;
    } else {
      // Max attempts reached - redirect to waiting
      return <Navigate to={carrying(ePath('/waiting'))} replace />;
    }
  }

  if ([
    EntrepreneurState.PENDING_SUMMARY,
    EntrepreneurState.PENDING_PASSED_PRESCORE,
    EntrepreneurState.PENDING_WITH_NOTES
  ].includes(entrepreneurUser.entrepreneur_state)) {
    if (entrepreneurUser.email_verified === false) {
      return <Navigate to={carrying(ePath('/verify-email'))} replace />;
    }
    return <Navigate to={carrying(ePath('/waiting'))} replace />;
  }

  if ([
    EntrepreneurState.REJECTED,
    EntrepreneurState.REJECTED_FAILED_PRESCORE,
    EntrepreneurState.SUSPENDED,
    EntrepreneurState.DELETED
  ].includes(entrepreneurUser.entrepreneur_state)) {
    // DISABLED users cannot access anything - redirect to login  
    return <Navigate to={carrying(ePath('/login'))} replace />;
  }

  // Only allow APPROVED users to see children
  if (entrepreneurUser.entrepreneur_state !== EntrepreneurState.APPROVED) {
    return <Navigate to={carrying(ePath('/waiting'))} replace />;
  }

  return <>{children}</>;
};

export const PendingOnlyGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <StateGuard
    allowedStates={[
      EntrepreneurState.PENDING_LINKEDIN_PARSE,
      EntrepreneurState.PENDING_CV_UPLOAD,
      EntrepreneurState.PENDING_ONBOARDING,
      EntrepreneurState.PENDING_SUMMARY,
      EntrepreneurState.PENDING_PASSED_PRESCORE,
      EntrepreneurState.PENDING_FAILED_PRESCORE,
      EntrepreneurState.PENDING_WITH_NOTES
    ]}
    redirectPath={ePath('/dashboard')}
    children={children}
  />
);

export const ActiveOnlyGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <StateGuard
    allowedStates={[EntrepreneurState.APPROVED]}
    redirectPath={ePath('/onboarding')}
    children={children}
  />
);